import { DEFAULT_LOCALE } from "../../constants";
import { computeLocaleWith } from "../../i18n";
import { Locale as LocaleObj } from "../../models";
import { setLocaleHeader } from "../../services/granularAxios";

import { useMaybeUserData } from "./user";

export type Locale = LocaleObj["locale"];
export { DEFAULT_LOCALE };

export const localeAccessError = new Error(
  "Attempted to access locale but it is not available",
);

// ####################
// ## useMaybeLocale ##
// ####################

export type ResultOfUseMaybeLocale = Locale | undefined;
export type UseMaybeLocale = () => ResultOfUseMaybeLocale;

/**
 * The returned value of this hook will contain
 * the current user locale, which may be undefined
 *
 * This hook is the single source of truth for the user locale.
 * This hook should always be used over any locale data from useUser.
 */
export const useMaybeLocale: UseMaybeLocale = () => {
  const user = useMaybeUserData();
  const defaultLanguage = user?.defaultLanguage;
  let maybeLocale: Locale | undefined;
  if (defaultLanguage !== undefined) {
    maybeLocale = computeLocaleWith(defaultLanguage).locale;
  }
  setLocaleHeader(maybeLocale);
  return maybeLocale;
};

// ###########################
// ## useManageLocaleHeader ##
// ###########################

export type ResultOfUseManageLocaleHeader = void;
export type UseManageLocaleHeader = () => ResultOfUseManageLocaleHeader;

/**
 * Call this hook to manage the locale ajax header.
 */
export const useManageLocaleHeader: UseManageLocaleHeader = () => {
  useMaybeLocale();
};

// ##############################
// ## useLocaleWithoutFallback ##
// ##############################

export type ResultOfUseLocaleWithoutFallback = Locale;
export type UseLocaleWithoutFallback = () => ResultOfUseLocaleWithoutFallback;

/**
 * The returned value of this hook will contain the current user locale.
 *
 * This hook is guaranteed to contain the locale or will throw.
 *
 * Use this over the locale data from `useUser` as this is guaranteed to be a valid locale
 * and this hook will not trigger a re-render for you unless the locale and only the locale changes.
 */
export const useLocaleWithoutFallback: UseLocaleWithoutFallback = () => {
  const locale = useMaybeLocale();
  if (locale === undefined) {
    throw localeAccessError;
  }
  return locale;
};

// ###############
// ## useLocale ##
// ###############

export type ResultOfUseLocale = Locale;
export type UseLocale = () => ResultOfUseLocale;

/**
 * The returned value of this hook will contain the current user locale or the fallback locale.
 *
 * This hook is guaranteed to contain a valid locale.
 *
 * Use this over the locale data from `useUser` as this is guaranteed to be a valid locale
 * and this hook will not trigger a re-render for you unless the locale and only the locale changes.
 */
export const useLocale: UseLocale = () => {
  const locale = useMaybeLocale();
  if (locale === undefined) {
    return DEFAULT_LOCALE;
  }
  return locale;
};
